'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { employeesAPI, exportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TableSkeleton } from '../components/LoadingSkeleton';
import SearchBar from '../components/SearchBar';
import { handleExport } from '../utils/exportHelper';
import ConfirmDialog from '../components/ConfirmDialog';
import '../styles/Employees.css';

function Employees() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    category: 'other',
    dailySalary: '',
    isActive: true,
  });

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await employeesAPI.getAll();
      if (response.data && response.data.employees) {
        setEmployees(response.data.employees);
      } else {
        console.error('Unexpected API response:', response.data);
        setEmployees([]);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      const message = err.response?.data?.message || err.message || 'Failed to fetch employees';
      showError(message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(employee => 
      employee.name.toLowerCase().includes(term) ||
      employee.role.toLowerCase().includes(term) ||
      (employee.category && employee.category.toLowerCase().includes(term)) ||
      (employee.dailySalary && employee.dailySalary.toString().includes(term))
    );
  }, [employees, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await employeesAPI.update(editingId, formData);
        success('Employee updated successfully');
      } else {
        await employeesAPI.create(formData);
        success('Employee created successfully');
      }
      setFormData({
        name: '',
        role: '',
        category: 'other',
        dailySalary: '',
        isActive: true,
      });
      setShowForm(false);
      setEditingId(null);
      fetchEmployees();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save employee';
      showError(message);
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      name: employee.name,
      role: employee.role,
      category: employee.category || 'other',
      dailySalary: employee.dailySalary || employee.monthlySalary || '', // Support old data
      isActive: employee.isActive,
    });
    setEditingId(employee._id);
    setShowForm(true);
  };

  const handleDeleteClick = (id, name) => {
    setDeleteTarget({ id, name });
  };

  const handleViewDetails = async (employee) => {
    setDetailsLoading(true);
    try {
      const response = await employeesAPI.getById(employee._id);
      setSelectedEmployee(response.data);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load employee details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await employeesAPI.delete(deleteTarget.id);
      success('Employee deleted successfully');
      fetchEmployees();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to delete employee';
      showError(message);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      role: '',
      category: 'other',
      dailySalary: '',
      isActive: true,
    });
  };

  const categoryOptions = [
    { value: 'chef', label: 'Chef' },
    { value: 'waiter', label: 'Waiter' },
    { value: 'helper', label: 'Helper' },
    { value: 'manager', label: 'Manager' },
    { value: 'driver', label: 'Driver' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="employees-container">
      <div className="page-header">
        <h1>Employee Management</h1>
        <div className="header-actions">
          {employees.length > 0 && (
            <button
              onClick={() => handleExport(
                exportAPI.exportEmployees,
                `employees_${new Date().toISOString().split('T')[0]}.xlsx`,
                () => success('Employees exported successfully'),
                (error) => showError(error)
              )}
              className="btn btn-secondary"
            >
              📥 Export to Excel
            </button>
          )}
          {user?.role === 'owner' && (
            <button onClick={() => setShowForm(!showForm)} className="btn">
              {showForm ? 'Cancel' : '+ New Employee'}
            </button>
          )}
        </div>
      </div>

      {!loading && employees.length > 0 && (
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name, role, category, or daily salary..."
        />
      )}

      {showForm && (
        <div className="form-container">
          <h2>{editingId ? 'Edit Employee' : 'Create New Employee'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Head Chef, Senior Waiter"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Daily Salary (₹) *</label>
                <input
                  type="number"
                  name="dailySalary"
                  value={formData.dailySalary}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="e.g., 500"
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn">
                {editingId ? 'Update' : 'Create'} Employee
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}


      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : filteredEmployees.length > 0 ? (
        <div className="employees-table-wrapper">
          <table className="employees-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Category</th>
                <th>Daily Salary</th>
                <th>Status</th>
                {user?.role === 'owner' && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(employee => (
                <tr key={employee._id} className={!employee.isActive ? 'inactive' : ''}>
                  <td>{employee.name}</td>
                  <td>{employee.role}</td>
                  <td>
                    <span className={`category-badge category-${employee.category || 'other'}`}>
                      {categoryOptions.find(opt => opt.value === (employee.category || 'other'))?.label || 'Other'}
                    </span>
                  </td>
                  <td>₹{(employee.dailySalary || employee.monthlySalary || 0).toFixed(2)}</td>
                  <td>
                    <span className={`status status-${employee.isActive ? 'active' : 'inactive'}`}>
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {user?.role === 'owner' && (
                    <td>
                      <button
                        onClick={() => handleViewDetails(employee)}
                        className="btn btn-small"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => handleEdit(employee)}
                        className="btn btn-small"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(employee._id, employee.name)}
                        className="btn btn-danger btn-small"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No employees found. Add your first employee!</div>
      )}
      {detailsLoading && <div className="employee-details-loading">Loading employee details...</div>}
      {selectedEmployee && (
        <div className="employee-details-overlay" role="dialog" aria-modal="true">
          <div className="employee-details-modal">
            <div className="employee-details-header">
              <div>
                <h2>{selectedEmployee.employee.name}</h2>
                <p>{selectedEmployee.employee.role} · {selectedEmployee.employee.category}</p>
              </div>
              <button className="btn btn-secondary" onClick={() => setSelectedEmployee(null)}>Close</button>
            </div>

            <div className="employee-details-summary">
              <div><strong>Daily salary</strong><span>₹{Number(selectedEmployee.employee.dailySalary || 0).toFixed(2)}</span></div>
              <div><strong>Attendance records</strong><span>{selectedEmployee.attendanceHistory.length}</span></div>
              <div><strong>Total salary</strong><span>₹{Number(selectedEmployee.salarySummary.totalAmount || 0).toFixed(2)}</span></div>
              <div><strong>Paid / unpaid</strong><span>₹{Number(selectedEmployee.salarySummary.paidAmount || 0).toFixed(2)} / ₹{Number(selectedEmployee.salarySummary.unpaidAmount || 0).toFixed(2)}</span></div>
            </div>

            <div className="employee-details-section">
              <h3>Attendance history</h3>
              {selectedEmployee.attendanceHistory.length === 0 ? <p className="no-records">No attendance records yet.</p> : (
                <div className="details-table-wrapper">
                  <table className="details-table"><thead><tr><th>Date</th><th>Status</th><th>Check-in</th><th>Notes</th></tr></thead>
                    <tbody>{selectedEmployee.attendanceHistory.map(record => <tr key={record._id}>
                      <td>{record.dateString}</td><td>{record.status === 'present' ? 'Present' : record.status}</td>
                      <td>{record.checkInTime ? new Date(record.checkInTime).toLocaleTimeString() : '-'}</td><td>{record.notes || '-'}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="employee-details-section">
              <h3>Salary history</h3>
              {selectedEmployee.salaryHistory.length === 0 ? <p className="no-records">No salary records yet.</p> : (
                <div className="details-table-wrapper">
                  <table className="details-table"><thead><tr><th>Date</th><th>Event</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>{selectedEmployee.salaryHistory.map(record => <tr key={record._id}>
                      <td>{new Date(record.workDate).toLocaleDateString()}</td><td>{record.eventName || record.eventId?.eventName || '-'}</td>
                      <td>₹{Number(record.amount || 0).toFixed(2)}</td><td>{record.isPaid ? 'Paid' : 'Unpaid'}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete employee"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default Employees;
